import { LayoutModule } from '@angular/cdk/layout';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {
  APP_INITIALIZER,
  ErrorHandler,
  isDevMode,
  LOCALE_ID,
  NgModule,
  NgZone,
  PLATFORM_ID,
} from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import {
  AngularFireAnalyticsModule,
  APP_NAME,
  APP_VERSION,
  CONFIG,
  DEBUG_MODE,
  UserTrackingService,
} from '@angular/fire/compat/analytics';
import {
  AngularFireAuth,
  AngularFireAuthModule,
} from '@angular/fire/compat/auth';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFirePerformanceModule } from '@angular/fire/compat/performance';
import {
  AngularFireRemoteConfigModule,
  DEFAULTS,
  SETTINGS,
} from '@angular/fire/compat/remote-config';
import { ReactiveFormsModule } from '@angular/forms';
import { AppDateFnsModule } from 'src/app/components/date-picker/date-adapter/app-date-fns.module';
import { DividerModule } from 'src/app/components/divider/divider.module';
import { GridListModule } from './components/grid-list/grid-list.module';
import { IconModule } from 'src/app/components/icon/icon.module';
import { FormFieldModule } from 'src/app/components/form-field/form-field.module';
import { InputModule } from 'src/app/components/input/input.module';
import { SelectModule } from './components/select/select.module';
import { SnackbarModule } from './components/snackbar/snackbar.module';
import { TooltipModule } from 'src/app/components/tooltip/tooltip.module';
import { SidenavModule } from './components/sidenav/sidenav.module';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, UrlSerializer } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IntercomModule } from '@supy-io/ngx-intercom';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxMaskModule } from 'ngx-mask';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CompanyContainerComponent } from './company-container/company-container.component';
import { CompanyPickerComponent } from './company-picker/company-picker.component';
import { CompaniesListComponent } from './components/companies-list/companies-list.component';
import { ContainerHeaderModule } from './components/container-header/container-header.module';
import { CookiesBannerModule } from './components/cookies-banner/cookies-banner.module';
import { FileComparisonDialogModule } from './components/file-comparison-dialog/file-comparison-dialog.module';
import { HeaderModule } from './components/header/header.module';
import { QrCodeSecretDialogModule } from './components/qr-code-secret-dialog/qr-code-secret-dialog.module';
import { SidebarModule } from './components/sidebar/sidebar.module';
import { AuthInterceptor } from './core/app.http-interceptor';
import { SentryErrorHandler } from './core/sentry-error-handler';
import { DashboardComponent } from './dashboard/dashboard.component';
import { AngularFireAuthFactory } from './firebase.factory';
import { NotificationPopupModule } from './notifications/notification-popup/notification-popup.module';
import { NotificationsListModule } from './notifications/notifications-list/notifications-list.module';
import { HomeComponent } from './pages/main-dashboard/home.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ErrorComponent } from './pages/error/error.component';
import { RestrictedComponent } from './pages/restricted/restricted.component';
import { CacheRouteReuseStrategy } from './pages/sick-note/cache-route-reuse.strategy';
import { VersionUrlSerializer } from './core/version-url-serializer';
import { ProfilePopupComponent } from './profile-popup/profile-popup.component';
import { FeatureFlagsService } from './services/feature-flags.service';
import { SharedModule } from './shared/shared.module';
import { SecurePipe } from './shared/utils/pipes';
import { ButtonModule } from './components/button/button.module';
import { CardModule } from 'src/app/components/card/card.module';
import { ListModule } from './components/list/list.module';
import { ProgressBarModule } from './components/progress-bar/progress-bar.module';

registerLocaleData(localePt, 'pt');

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    ProfilePopupComponent,
    HomeComponent,
    SecurePipe,
    CompanyPickerComponent,
    CompaniesListComponent,
    CompanyContainerComponent,
    NotFoundComponent,
    ErrorComponent,
    RestrictedComponent,
  ],
  imports: [
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFirestoreModule,
    AngularFirePerformanceModule,
    AngularFireAnalyticsModule,
    AngularFireRemoteConfigModule,
    BrowserModule,
    SharedModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    AppDateFnsModule,
    LayoutModule,
    GridListModule,
    IconModule,
    SnackbarModule,
    DividerModule,
    SidenavModule,
    HttpClientModule,
    FormFieldModule,
    SelectModule,
    InputModule,
    TooltipModule,
    ReactiveFormsModule,
    ButtonModule,
    ListModule,
    ProgressBarModule,
    LoggerModule.forRoot({
      serverLoggingUrl: '/api/logs',
      level: environment.production
        ? NgxLoggerLevel.ERROR
        : NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.OFF,
      enableSourceMaps: true,
    }),
    IntercomModule.forRoot({
      appId: environment.intercomAppId,
      updateOnRouterChange: true, // will automatically run `update` on router event changes. Default: `false`
    }),
    // ngx-mask root config. Every `<app-input>` uses `NgxMaskModule.forChild()`
    // internally, so the single global `NGX_MASK_CONFIG` registered here serves
    // every consumer; lazy modules no longer call `forRoot()` themselves.
    NgxMaskModule.forRoot(),
    NotificationsListModule,
    NotificationPopupModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    SidebarModule,
    ContainerHeaderModule,
    HeaderModule,
    QrCodeSecretDialogModule,
    FileComparisonDialogModule,
    CookiesBannerModule,
    CardModule,
  ],
  providers: [
    Title,
    {
      provide: RouteReuseStrategy,
      useClass: CacheRouteReuseStrategy,
    },
    {
      provide: UrlSerializer,
      useClass: VersionUrlSerializer,
    },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    {
      provide: CONFIG,
      useValue: {
        send_page_view: false,
      },
    },
    { provide: APP_NAME, useValue: environment.appName },
    { provide: APP_VERSION, useValue: environment.version },
    { provide: UserTrackingService },
    { provide: DEBUG_MODE, useValue: false },
    { provide: DEFAULTS, useValue: { loginMode: 'email' } },
    {
      provide: SETTINGS,
      useFactory: () =>
        isDevMode() ? { minimumFetchIntervalMillis: 10_000 } : {},
    },
    {
      provide: 'angularFireAuthFactory',
      deps: [PLATFORM_ID, NgZone, AngularFireAuth],
      useClass: AngularFireAuthFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (featureFlagsService: FeatureFlagsService) => () =>
        featureFlagsService.load(),
      deps: [FeatureFlagsService],
      multi: true,
    },
  ],
  exports: [SecurePipe],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {}
}
